import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Basic from '../../Components/Basic/Basic';
import Common from '../../services/common.js';
import dayjs from 'dayjs';
import { useToast } from "../../Utils/toastContext";

const normalizeDocuments = (docs) => {
  if (!Array.isArray(docs)) return [];

  return docs
    .map((doc, index) => {
      if (typeof doc === 'string') {
        return { doc_id: String(index + 1), doc_name: doc };
      }

      if (doc && typeof doc === 'object') {
        return {
          doc_id: String(doc.doc_id ?? doc.id ?? index + 1),
          doc_name: doc.doc_name ?? doc.name ?? doc.title ?? '',
        };
      }

      return null;
    })
    .filter((doc) => doc && doc.doc_name);
};

// ── Field component lives outside to prevent focus-loss on re-render ──
const Field = ({ id, label, type = 'text', placeholder, value, onChange, error }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-semibold text-gray-800">
      {label}
      <span className="text-red-500 ml-1">*</span>
    </label>
    <input
      id={id}
      name={id}
      type={type}
      placeholder={placeholder}
      value={value || ''}
      onChange={onChange}
      autoComplete="off"
      required
      className={[
        'w-full px-4 py-3 rounded-lg border text-sm text-gray-900 bg-white',
        'transition-all duration-150 outline-none placeholder-gray-400',
        'focus:ring-2 focus:ring-offset-0',
        error
          ? 'border-red-400 bg-red-50 focus:ring-red-300 focus:border-red-400'
          : 'border-gray-300 hover:border-gray-500 focus:ring-gray-900 focus:border-gray-900',
      ].join(' ')}
    />
    {error && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {error}
      </p>
    )}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-3 mb-5">
    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">
      {children}
    </span>
    <div className="flex-1 h-px bg-gray-100" />
  </div>
);

const EmailESign = () => {
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const [form, setForm] = useState({});
  const [confirm, setConfirm] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [approvalDocList, setApprovalDocList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [isApprovalValid, setIsApprovalValid] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [docChecked, setDocChecked] = useState(new Set());
  const [documents, setDocuments] = useState([]);

  const approval = searchParams.get('approval');
  const refText = searchParams.get('ref');
  const subject = searchParams.get('subject');
  const doc_name = searchParams.get('doc');
  const req_token = searchParams.get('req_token') || searchParams.get('token');

  const docList = doc_name
    ? doc_name.split(',').map((d) => d.trim()).filter(Boolean)
    : [];

  const token = localStorage.getItem('systemToken') || '';
  const currentDate = dayjs().format('DD/MM/YYYY');

  const displayDocuments = useMemo(() => {
    if (documents.length > 0) return documents;
    if (approvalDocList.length > 0) return normalizeDocuments(approvalDocList);
    if (docList.length > 0) return normalizeDocuments(docList);
    return [];
  }, [documents, approvalDocList, docList]);

  useEffect(() => {
    document.title = 'Email E-Signature || Caprock Merchant Platform';
    setForm((prev) => ({ ...prev, curdate: dayjs().format('YYYY-MM-DD') }));
    checkApprovalValid(approval || 0);
    checkSignatureDetails(req_token);
  }, [approval, req_token, token]);

  const checkSignatureDetails = async (requestToken) => {
    if (!requestToken) {
      return;
    }

    setIsLoadingDocs(true);

    try {
      const response = await Common.esign_request_details(requestToken, token);

      if (response?.success === 200) {
        const apiDocs =
          response.data?.documents ||
          response.documents ||
          response.data?.approval_doc_list ||
          [];

        setDocuments(normalizeDocuments(apiDocs));
      } else {
        console.error('E-signature details failed:', response);
      }
    } catch (error) {
      console.error('Failed to load e-signature documents:', error);
    } finally {
      setIsLoadingDocs(false);
    }
  };

  const checkApprovalValid = (approvalId) => {
    if (!approvalId || approvalId <= 0) {
      setIsApprovalValid(true);
      return;
    }

    Common.is_approval_valid(approvalId, token)
      .then((response) => {
        if (response?.success === 200) {
          setIsApprovalValid(true);

          const approvalData = response.data || {};
          const docListFromApproval =
            approvalData.approval_doc_list ||
            approvalData.documents ||
            [];

          if (docListFromApproval.length > 0) {
            setApprovalDocList(docListFromApproval);
          }

          if (response.master?.approval_response) {
            try {
              const apRes = JSON.parse(response.master.approval_response);
              if (apRes) {
                setForm((prev) => ({
                  ...prev,
                  e_name: apRes.e_name || '',
                  e_email: apRes.e_email || '',
                  e_phone_no: apRes.e_phone_no || '',
                  e_dob: apRes.e_dob || '',
                }));
              }
            } catch (e) {
              console.log('Parse error:', e);
            }
          }
        } else {
          showToast('Link expired! Redirecting…', 'error');
          setTimeout(() => {
            window.location.href = 'https://caprock.com.au';
          }, 2500);
        }
      })
      .catch(() => {
        showToast('Failed to validate approval link', 'error');
      });
  };

  const validate = () => {
    const errs = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.e_name?.trim()) errs.e_name = 'Full name is required';
    if (!form.e_email?.trim()) errs.e_email = 'Email address is required';
    else if (!emailRe.test(form.e_email)) errs.e_email = 'Please enter a valid email address';

    const ph = form.e_phone_no || '';
    if (!ph) errs.e_phone_no = 'Phone number is required';
    else if (isNaN(ph)) errs.e_phone_no = 'Only numbers are allowed';
    else if (ph.length < 7 || ph.length > 12) errs.e_phone_no = 'Phone must be 7–12 digits';

    if (!form.e_dob) errs.e_dob = 'Date of birth is required';
    if (!confirm) errs.confirm = 'You must agree to the conditions';

    if (displayDocuments.length > 0 && docChecked.size === 0) {
      errs.documents = 'Please select at least one document';
    }

    return errs;
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!isApprovalValid) {
      showToast('Please wait for approval validation', 'error');
      return;
    }

    const errs = validate();
    setFieldErrors(errs);
    setConfirmError(errs.confirm || errs.documents || '');

    if (Object.keys(errs).length > 0) {
      showToast(errs.documents || 'Please fill all required fields.', 'error');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    for (const key in form) formData.append(key, form[key]);
    formData.append('user_token', token);
    formData.append('approval', approval);
    formData.append('curdate', dayjs().format('YYYY-MM-DD'));
    formData.append('agreement_text', doc_name);
    formData.append('subject', subject);
    formData.append('log_id', refText);
    formData.append('doc_checked', [...docChecked].join(','));
    formData.append('req_token', req_token);

    fetch(Common.base_url() + '/e-signature-submit', { method: 'POST', body: formData })
      .then((r) => r.json())
      .then((data) => {
        setIsLoading(false);
        if (data.success === 200) {
          showToast(data.message || 'E-Signature submitted successfully!', 'success');
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        } else {
          showToast(data.message || 'Submission failed. Please try again.', 'error');
        }
      })
      .catch(() => {
        setIsLoading(false);
        showToast('Failed to submit form. Please check your connection.', 'error');
      });
  };

  if (!isApprovalValid) {
    return (
      <>
        <Basic />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-md p-12 text-center max-w-sm w-full">
            <div className="flex justify-center mb-5">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin" />
            </div>
            <p className="text-gray-900 font-semibold text-lg">Validating your link</p>
            <p className="text-gray-400 text-sm mt-1">Please wait a moment…</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Basic />
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-900 rounded-2xl mb-4">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Email E-Signature</h1>
            <p className="text-gray-500 mt-2 text-sm">Complete the form below to sign your documents</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 bg-gray-900 rounded-t-2xl">
              <span className="text-sm font-semibold text-white flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                E-Signature Form
              </span>
              <span className="text-xs text-gray-300 bg-gray-800 border border-gray-700 rounded-full px-3 py-1 font-medium">
                {currentDate}
              </span>
            </div>

            <form onSubmit={submitForm} noValidate className="p-6 sm:p-8 space-y-8">
              <div>
                <SectionLabel>Personal Details</SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    id="e_name"
                    label="Full Name"
                    placeholder="Enter your full name"
                    value={form.e_name}
                    error={fieldErrors.e_name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, e_name: e.target.value }));
                      setFieldErrors((p) => ({ ...p, e_name: '' }));
                    }}
                  />
                  <Field
                    id="e_email"
                    label="Email Address"
                    type="email"
                    placeholder="you@example.com"
                    value={form.e_email}
                    error={fieldErrors.e_email}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, e_email: e.target.value }));
                      setFieldErrors((p) => ({ ...p, e_email: '' }));
                    }}
                  />
                  <Field
                    id="e_phone_no"
                    label="Phone Number"
                    type="tel"
                    placeholder="e.g. 0412345678"
                    value={form.e_phone_no}
                    error={fieldErrors.e_phone_no}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, e_phone_no: e.target.value }));
                      setFieldErrors((p) => ({ ...p, e_phone_no: '' }));
                    }}
                  />
                  <Field
                    id="e_dob"
                    label="Date of Birth"
                    type="date"
                    value={form.e_dob}
                    error={fieldErrors.e_dob}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, e_dob: e.target.value }));
                      setFieldErrors((p) => ({ ...p, e_dob: '' }));
                    }}
                  />
                </div>
              </div>

              <div>
                <SectionLabel>Documents selected for signing</SectionLabel>

                {isLoadingDocs ? (
                  <div className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
                    <span className="text-sm text-gray-500">Loading documents…</span>
                  </div>
                ) : displayDocuments.length > 0 ? (
                  <div className="space-y-2">
                    {displayDocuments.map((doc, i) => (
                      <label
                        key={`${doc.doc_id}-${i}`}
                        className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-900 text-white text-xs font-bold flex items-center justify-center">
                          {i + 1}
                        </span>
                        <input
                          type="checkbox"
                          checked={docChecked.has(String(doc.doc_id))}
                          onChange={() => {
                            setDocChecked((prev) => {
                              const next = new Set(prev);
                              const id = String(doc.doc_id);
                              if (next.has(id)) next.delete(id);
                              else next.add(id);
                              return next;
                            });
                            setFieldErrors((p) => ({ ...p, documents: '' }));
                          }}
                          className="w-4 h-4 accent-gray-900 cursor-pointer shrink-0"
                        />
                        <span className="text-sm text-gray-700">{doc.doc_name}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3.5 rounded-xl border border-red-100 bg-red-50 text-sm text-red-700">
                      No documents found. Please open the link from your email or contact support.
                    </div>
                  </div>
                )}

                {fieldErrors.documents && (
                  <p className="text-xs text-red-500 mt-2">{fieldErrors.documents}</p>
                )}
              </div>

              <div>
                <SectionLabel>Agreement</SectionLabel>

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="confirm_check"
                    checked={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.checked);
                      setConfirmError('');
                      setFieldErrors((p) => ({ ...p, confirm: '' }));
                    }}
                    className="mt-1 w-4 h-4 accent-gray-900 cursor-pointer shrink-0"
                  />
                  <label htmlFor="confirm_check" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                    I confirm that I have received, read, and agree to be bound by the terms and conditions contained in the selected documents listed above.
                  </label>
                </div>

                {confirmError && (
                  <p className="text-xs text-red-500 flex items-center gap-1 mt-2">
                    <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {confirmError}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || isLoadingDocs || displayDocuments.length === 0}
                className={[
                  'w-full py-4 px-6 rounded-xl font-semibold text-white text-sm tracking-wide',
                  'flex items-center justify-center gap-2 transition-all duration-200',
                  isLoading || isLoadingDocs || displayDocuments.length === 0
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-gray-900 hover:bg-gray-800 shadow-md hover:shadow-lg active:scale-[0.99]',
                ].join(' ')}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    Submit E-Signature
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-gray-400 mt-5 flex items-center justify-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Your information is encrypted and secure
          </p>
        </div>
      </div>
    </>
  );
};

export default EmailESign;
